import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Textarea } from './ui/textarea';
import { Badge } from './ui/badge';
import { Loader2, Check, FileText } from 'lucide-react';

const EditableOverviewTab = ({ event, onUpdate }) => {
  const [room, setRoom] = useState(event?.room || '');
  const [description, setDescription] = useState(event?.description || '');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  
  // Track pending changes and backup timer
  const pendingChanges = useRef({});
  const backupTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  // Only sync with parent on initial mount, not on every update
  useEffect(() => {
    if (isInitialMount.current) {
      setRoom(event?.room || '');
      setDescription(event?.description || '');
      isInitialMount.current = false;
    }
  }, [event?.room, event?.description]);

  // Cleanup backup timer on unmount
  useEffect(() => {
    return () => {
      if (backupTimeoutRef.current) {
        clearTimeout(backupTimeoutRef.current);
      }
    };
  }, []);

  // Perform the actual save
  const doSave = useCallback(async () => {
    if (Object.keys(pendingChanges.current).length === 0) return;
    
    setSaving(true);
    const changesToSave = { ...pendingChanges.current };
    pendingChanges.current = {};
    
    try {
      await onUpdate(changesToSave);
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save:', error);
      // Restore pending changes on error
      pendingChanges.current = { ...changesToSave, ...pendingChanges.current };
    } finally {
      setSaving(false);
    }
  }, [onUpdate]);

  // Queue a change - starts/resets the backup timer
  const queueChange = useCallback((updates) => {
    pendingChanges.current = { ...pendingChanges.current, ...updates };
    
    // Reset backup timer
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    
    // Backup save after 5 seconds of inactivity
    backupTimeoutRef.current = setTimeout(() => {
      doSave();
    }, 1000);
  }, [doSave]);

  // Save immediately (for blur events)
  const saveNow = useCallback(() => {
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    doSave();
  }, [doSave]);

  const handleRoomChange = (value) => {
    setRoom(value);
    queueChange({ room: value });
  };

  const handleDescriptionChange = (value) => {
    setDescription(value);
    queueChange({ description: value });
  };

  return (
    <div className="space-y-6">
      {/* Save indicator */}
      <div className="flex items-center justify-end h-6">
        {saving && (
          <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
            <Loader2 className="h-3 w-3 mr-1 animate-spin" />
            Saving...
          </Badge>
        )}
        {saved && !saving && (
          <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
            <Check className="h-3 w-3 mr-1" />
            Saved
          </Badge>
        )}
      </div>

      {/* Room/Space */}
      <Card className="border-border/50 bg-card/50">
        <CardContent className="pt-6">
          <Label htmlFor="room" className="text-sm text-muted-foreground uppercase tracking-wider mb-2 block">
            Room / Space
          </Label>
          <Input
            id="room"
            value={room}
            onChange={(e) => handleRoomChange(e.target.value)}
            onBlur={saveNow}
            placeholder="Enter room or space name..."
            className="bg-background/50"
            data-testid="edit-room-input"
          />
        </CardContent>
      </Card>

      {/* Description */}
      <Card className="border-border/50 bg-card/50">
        <CardHeader>
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <FileText className="h-5 w-5 text-primary" />
            Description
          </CardTitle>
        </CardHeader>
        <CardContent>
          <Textarea
            value={description}
            onChange={(e) => handleDescriptionChange(e.target.value)}
            onBlur={saveNow}
            placeholder="Enter event description..."
            rows={6}
            className="bg-background/50"
            data-testid="edit-description-input"
          />
        </CardContent>
      </Card>
    </div>
  );
};

export default EditableOverviewTab;
